import { ProviderId } from 'firebase/auth';
import { Nullish, ValuesType } from 'utility-types';

// Import types
import type { Genre } from 'types/api/products/product';

export type SearchListParams = {
	url: string;
	genres: Genre[] | null;
	item_name: string;
	item_variant: number | null;
	release_year: number | null;
	country: string | null;
	runtime: number | null;
};

export type EpgItemClickParams = {
	url: string;
	item_name: string;
	runtime: number;
	label: string | undefined;
	item_brand1?: string;
};

export type ChannelEventParams = { url: string; genres: Genre[] | null; item_name: string };

export type FilmEventParams = {
	item_name: string;
	item_variant: number;
	release_year: number;
	country: string;
	runtime: number;
	genres: Genre[] | null;
	label: string;
};

export type ViewProductParams = {
	item_name: string;
	item_id: string;
	release_year: number;
	country: string;
	genres: Genre[];
	price: number | null;
};

export type PacketEventCommonParams = {
	item_name: string;
	item_id: string;
	price: number | undefined;
	subtype: string;
};

export enum EventGTM {
	InteractWithFilm = 'interact_with_film',
	ClickCarousel = 'click_carousel',
	ClickBanner = 'click_banner',
	ClickSearchList = 'click_search_list',
	ClickSearchResults = 'click_search_results',
	ClickTvShow = 'click_tvshow',
	ClickTelevision = 'click_telewizja',
	ClickHeaderMenu = 'header_menu',
	SeeCta = 'poznaj_CTA',
	SeeCtaFaq = 'poznaj_FAQ',
	Register = 'registration_complete',
	Login = 'login',
	Purchase = 'purchase',
	AddToCart = 'add_to_cart',
	PaymentStart = 'payment_start',
	CancelPackage = 'cancel_package',
	RenewPackage = 'renew_package',
	RetryPayment = 'retry_payment',
	CancelPayment = 'cancel_payment',
	ViewItem = 'view_item',
	BeginCheckout = 'begin_checkout',
	StepRegulation = 'step_regulamin',
	StepData = 'step_dane',
	LeaveCheckout = 'leave_checkout',
	AddPaymentInfo = 'add_payment_info'
}

export enum GTMSegmentation {
	Film = 'film',
	Global = 'global',
	Account = 'account'
}

export enum GTMAction {
	Click = 'click',
	Form = 'form',
	Video = 'video'
}

export enum GTMUserType {
	Vectra = 'vectra',
	External = 'external'
}

export enum GtmPaymentStatus {
	Success = 'success',
	Pending = 'pending'
}

export enum GtmPaymentType {
	CreditCard = 'Credit card'
}

type PriceData = {
	value: number;
	currency: string;
};

type LoginEventData = {
	segmentation: GTMSegmentation.Account;
	action: GTMAction.Form;
	user_type: GTMUserType;
	method: ValuesType<typeof ProviderId> | 'email';
};

type LoginEvent = {
	event: EventGTM.Login;
	data: LoginEventData;
};

type ClickBannerEventData = {
	segmentation: GTMSegmentation.Film;
	action: GTMAction.Click;
	page_url: string;
	url: string;
	item_name: string;
};

type ClickBannerEvent = {
	event: EventGTM.ClickBanner;
	data: ClickBannerEventData;
};

type ClickCarouselEventData = {
	segmentation: GTMSegmentation.Film;
	action: GTMAction.Click;
	page_url: string;
	url: string;
	label: string;
};

type ClickCarouseEvent = {
	event: EventGTM.ClickCarousel;
	data: ClickCarouselEventData;
};

type ClickSearchEventData = {
	segmentation: GTMSegmentation.Film;
	action: GTMAction.Click;
	page_url: string;
	url: string;
	item_name: string;
	item_variant: number | Nullish;
	release_year: number | Nullish;
	country: string | Nullish;
	runtime: number | Nullish;
};

type ClickSearchListEvent = {
	event: EventGTM.ClickSearchList;
	data: ClickSearchEventData;
};

type ClickSearchResultsEvent = {
	event: EventGTM.ClickSearchResults;
	data: ClickSearchEventData;
};

type ClickTvShowEventData = {
	segmentation: GTMSegmentation.Film;
	action: GTMAction.Click;
	page_url: string;
	url: string;
	item_name: string;
	label: string | undefined;
	runtime?: number | Nullish;
};

type ClickTvShowEvent = {
	event: EventGTM.ClickTvShow;
	data: ClickTvShowEventData;
};

type ClickHeaderMenuEventData = {
	segmentation: GTMSegmentation.Global;
	action: GTMAction.Click;
	page_url: string;
	url: string;
	text: string;
};

type ClickHeaderMenuEvent = {
	event: EventGTM.ClickHeaderMenu;
	data: ClickHeaderMenuEventData;
};

type ClickTelevisionEventData = {
	segmentation: GTMSegmentation.Film;
	action: GTMAction.Click;
	page_url: string;
	url: string;
	item_name: string;
};

type ClickTelevisionEvent = {
	event: EventGTM.ClickTelevision;
	data: ClickTelevisionEventData;
};

type InteractWithFilmEventData = {
	segmentation: GTMSegmentation.Film;
	action: GTMAction.Video;
	page_url: string;
	item_name: string;
	item_variant: number | Nullish;
	release_year: number;
	country: string;
	runtime: number | Nullish;
	label?: string;
};

type InteractWithFilmEven = {
	event: EventGTM.InteractWithFilm;
	data: InteractWithFilmEventData;
};

type SeeCtaFaqEventData = {
	segmentation: GTMSegmentation.Global;
	action: GTMAction.Click;
	page_url: string;
	text: string;
};

type SeeCtaFaqEvent = {
	event: EventGTM.SeeCtaFaq;
	data: SeeCtaFaqEventData;
};

type RegisterEventData = {
	segmentation: GTMSegmentation.Account;
	action: GTMAction.Form;
};

type RegisterEvent = {
	event: EventGTM.Register;
	data: RegisterEventData;
};

type BaseItem = {
	item_id: string;
	item_name: string;
	currency: string;
	item_category: string;
	price: number;
	quantity: number;
};

type ProductPurchaseEventData = {
	transaction_id: number;
	status: GtmPaymentStatus;
	items: BaseItem[];
} & PriceData;

type ProductPurchaseEvent = {
	event: EventGTM.Purchase;
	data: ProductPurchaseEventData;
};

type ProductPaymentItem = BaseItem & {
	country: string;
	release_year: number | null;
	[key: `item_brand${number}`]: string;
};

export type ProductPaymentEventData = {
	items: ProductPaymentItem[];
} & PriceData;

type ProductPaymentStartEvent = {
	event: EventGTM.PaymentStart;
	data: ProductPaymentEventData;
};

type ProductAddToCartEvent = {
	event: EventGTM.AddToCart;
	data: ProductPaymentEventData;
};

type PacketItem = BaseItem & {
	item_brand: string;
};

type PacketsCancelEvent = {
	event: EventGTM.CancelPackage;
	data: { items: PacketItem[] };
};

type PacketsRenewEvent = {
	event: EventGTM.RenewPackage;
	data: { items: PacketItem[] };
};

type PaymentLifecycleData = {
	page_url: string;
	item_category: string;
	item_name: string;
};

type PaymentCancelEvent = {
	event: EventGTM.CancelPayment;
	data: PaymentLifecycleData;
};

type PaymentRetryEvent = {
	event: EventGTM.RetryPayment;
	data: PaymentLifecycleData;
};

type ProductViewItemEvent = {
	event: EventGTM.ViewItem;
	data: { items: ProductPaymentItem[] };
};

type ViewPacketEvent = {
	event: EventGTM.ViewItem;
	data: { items: PacketItem[] };
};

type PacketsBeginCheckoutEvent = {
	event: EventGTM.BeginCheckout;
	data: { items: PacketItem[] };
};

type PacketsStepRegulationEvent = {
	event: EventGTM.StepRegulation;
	data: { items: PacketItem[] };
};

type PacketsStepDataEvent = {
	event: EventGTM.StepData;
	data: { items: PacketItem[] };
};

type PacketsCommonEventData = {
	items: PacketItem[];
} & PriceData;

type PacketsPurchaseEvent = {
	event: EventGTM.Purchase;
	data: PacketsCommonEventData & { status: GtmPaymentStatus; transaction_id: number | null };
};

type PacketsAddToCartEvent = {
	event: EventGTM.AddToCart;
	data: PacketsCommonEventData;
};

type PacketsPaymentStartEvent = {
	event: EventGTM.PaymentStart;
	data: PacketsCommonEventData & { payment_type: GtmPaymentType.CreditCard };
};

type PacketsPaymentInfoEvent = {
	event: EventGTM.AddPaymentInfo;
	data: PacketsCommonEventData & { payment_type: GtmPaymentType.CreditCard };
};

type PacketsLeaveCheckoutEvent = {
	event: EventGTM.LeaveCheckout;
	data: PacketsCommonEventData;
};

type ProductLeaveCheckoutEvent = {
	event: EventGTM.LeaveCheckout;
	data: ProductPaymentEventData;
};

export type GtmEvent =
	| LoginEvent
	| ClickBannerEvent
	| ClickCarouseEvent
	| ClickSearchListEvent
	| ClickSearchResultsEvent
	| ClickTvShowEvent
	| ClickHeaderMenuEvent
	| ClickTelevisionEvent
	| InteractWithFilmEven
	| SeeCtaFaqEvent
	| RegisterEvent
	| PacketsCancelEvent
	| PacketsRenewEvent
	| ProductPurchaseEvent
	| ProductPaymentStartEvent
	| ProductAddToCartEvent
	| PaymentCancelEvent
	| PaymentRetryEvent
	| ProductViewItemEvent
	| PacketsPurchaseEvent
	| ViewPacketEvent
	| PacketsAddToCartEvent
	| PacketsPaymentStartEvent
	| PacketsBeginCheckoutEvent
	| PacketsStepRegulationEvent
	| PacketsStepDataEvent
	| PacketsAddToCartEvent
	| PacketsLeaveCheckoutEvent
	| ProductLeaveCheckoutEvent
	| PacketsPaymentInfoEvent;
